
import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Zap, Brain, Lock, Cloud, Smartphone, RefreshCw, Globe } from 'lucide-react';
import { fadeIn, staggerContainer, getReducedMotionVariants } from '../utils/motion';
import { Card, CardContent } from '@/components/ui/card';

interface TechnologyPageProps {
  reducedMotion: boolean;
}

const TechnologyPage: React.FC<TechnologyPageProps> = ({ reducedMotion }) => {
  const technologyFeatures = [
    {
      icon: Brain,
      title: "AI-Powered Intelligence",
      description: "C.L.A.R.A. AI assistant provides 24/7 support with advanced natural language processing",
      details: ["OpenAI GPT-4 integration", "Context-aware responses", "Continuous learning"]
    },
    {
      icon: Zap,
      title: "Real-Time Performance",
      description: "Lightning-fast calculations and instant data updates",
      details: ["Sub-second response times", "Optimized algorithms", "Edge computing"]
    },
    {
      icon: Cloud,
      title: "Cloud Infrastructure",
      description: "Enterprise-grade cloud architecture built on Supabase",
      details: ["99.99% uptime SLA", "Auto-scaling", "Global CDN"]
    },
    {
      icon: Smartphone,
      title: "Mobile-First Design",
      description: "Fully responsive interface works seamlessly on any device",
      details: ["Progressive Web App", "Touch-optimized", "Offline capabilities"]
    }
  ];

  const securityFeatures = [
    {
      icon: Shield,
      title: "Bank-Level Encryption",
      description: "All data encrypted in transit and at rest using AES-256",
      highlight: "Military-grade security"
    },
    {
      icon: Lock,
      title: "Privacy First",
      description: "Your financial data never leaves our secure servers",
      highlight: "GDPR compliant"
    },
    {
      icon: RefreshCw,
      title: "Regular Audits",
      description: "Continuous security monitoring and penetration testing",
      highlight: "SOC 2 certified"
    },
    {
      icon: Globe,
      title: "Secure Infrastructure",
      description: "Multi-region redundancy with automated backups",
      highlight: "99.99% uptime"
    }
  ];

  const techStack = [
    { name: "React 18", category: "Frontend" },
    { name: "TypeScript", category: "Language" },
    { name: "Tailwind CSS", category: "Styling" },
    { name: "Framer Motion", category: "Animation" },
    { name: "Supabase", category: "Backend" },
    { name: "PostgreSQL", category: "Database" },
    { name: "OpenAI GPT-4", category: "AI/ML" },
    { name: "Vercel", category: "Hosting" }
  ];

  const reliabilityStats = [
    { value: "99.99%", label: "Uptime" },
    { value: "<100ms", label: "Response Time" },
    { value: "256-bit", label: "Encryption" },
    { value: "24/7", label: "Monitoring" }
  ];

  return (
    <div className="space-y-16">
      {/* Header */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-4"
      >
        <h2 className="text-4xl font-bold bg-gradient-to-r from-yellow-400 via-purple-500 to-cyan-500 bg-clip-text text-transparent">
          Enterprise Technology & Security
        </h2>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Built with cutting-edge technology and fortified with bank-level security
        </p>
      </motion.div>

      {/* Reliability Stats */}
      <motion.div
        variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
      >
        {reliabilityStats.map((stat, index) => (
          <motion.div
            key={stat.label}
            variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.4), reducedMotion)}
          >
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>

      {/* Technology Features */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0.2, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="space-y-6"
      >
        <h3 className="text-2xl font-bold text-center">Advanced Technology</h3>
        <motion.div
          variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {technologyFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.4), reducedMotion)}
              whileHover={!reducedMotion ? { y: -5, transition: { type: "spring", stiffness: 300 } } : {}}
            >
              <Card className="h-full">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10">
                      <feature.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h4 className="font-semibold text-lg">{feature.title}</h4>
                  </div>
                  <p className="text-muted-foreground">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, i) => (
                      <li key={i} className="text-sm flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Security Features */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0.3, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="space-y-6"
      >
        <h3 className="text-2xl font-bold text-center">Bank-Level Security</h3>
        <motion.div
          variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {securityFeatures.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={getReducedMotionVariants(fadeIn('up', index * 0.1, 0.4), reducedMotion)}
            >
              <Card className="h-full">
                <CardContent className="p-6 space-y-4 text-center">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 mx-auto">
                    <feature.icon className="w-6 h-6 text-green-500" />
                  </div>
                  <div className="font-semibold">{feature.title}</div>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                  <div className="pt-2 border-t border-border">
                    <span className="text-xs font-medium text-green-500">{feature.highlight}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Tech Stack */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0.4, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="space-y-6"
      >
        <h3 className="text-2xl font-bold text-center">Built with Modern Technology</h3>
        <motion.div
          variants={getReducedMotionVariants(staggerContainer(0.05, 0.1), reducedMotion)}
          className="flex flex-wrap justify-center gap-3"
        >
          {techStack.map((tech, index) => (
            <motion.div
              key={tech.name}
              variants={getReducedMotionVariants(fadeIn('up', index * 0.05, 0.3), reducedMotion)}
            >
              <div className="px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <span className="font-medium text-sm">{tech.name}</span>
                <span className="text-xs text-muted-foreground ml-2">• {tech.category}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Bottom Section */}
      <motion.div
        variants={getReducedMotionVariants(fadeIn('up', 0.5, 0.4), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="text-center space-y-4 pt-8 border-t border-border"
      >
        <h3 className="text-2xl font-bold">Your Data is Safe With Us</h3>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          We take security seriously. Your financial information is protected with the same level of encryption 
          and security used by major financial institutions. We never sell your data and maintain strict privacy standards.
        </p>
      </motion.div>
    </div>
  );
};

export default TechnologyPage;
